/**
 * purgeOrder — RPC contract for the purgeOrder callable, the
 * only path that destroys an order. A Firestore delete rule could express
 * neither the server-side eligibility guard nor the cascade that unwinds
 * everything the order minted, so the authorization is the callable itself.
 *
 *
 * Fields justified:
 *   eventId — the order's parent event; the order path is
 *             events/{eventId}/orders/{orderId} and the id alone does not
 *             locate the document.
 *   orderId — the order to purge; also the audit document's own id.
 * Excluded:
 *   a confirmation flag — the operator's acknowledgement is a surface
 *           concern, and a boolean the caller sets to true is no guard.
 *
 * Output fields justified:
 *   deletedTicketCount     — what the operator destroyed, the headline of
 *                            the irreversible act they just authorised.
 *   quotaReturned          — per typeId, the capacity handed back; the audit
 *                            record's own declaration, so the number the
 *                            caller reads and the number the record keeps
 *                            cannot drift into two shapes.
 *   storageObjectsDeleted  — how many rendered artifacts the cascade
 *                            removed, the one leg that lives outside
 *                            Firestore and therefore outside any listener.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { QuotaReturnedSchema } from "./order-purge.entity.ts";
import { Id } from "./workspace.entity.ts";

export const PurgeOrderInput = z
	.object({
		orgId: Id,
		eventId: z
			.string()
			.min(1)
			.describe("the event the order being purged belongs to"),
		orderId: z
			.string()
			.min(1)
			.describe(
				"the order to erase — also the id of the audit document the purge writes",
			),
	})
	.strict();
export type PurgeOrderInput = z.infer<typeof PurgeOrderInput>;

export const PurgeOrderOutput = z
	.object({
		deletedTicketCount: z
			.number()
			.int()
			.min(0)
			.describe("how many ticket documents the purge destroyed"),
		quotaReturned: QuotaReturnedSchema,
		storageObjectsDeleted: z
			.number()
			.int()
			.min(0)
			.describe(
				"how many objects the cascade removed under the order's Storage prefix",
			),
	})
	.strict();
export type PurgeOrderOutput = z.infer<typeof PurgeOrderOutput>;
